import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { API, useAuth } from "../App";
import { toast } from "sonner";
import { Briefcase, Package, Plus, ExternalLink, LogOut, Loader2, AlertTriangle, Building2, ShoppingBag, Trash2 } from "lucide-react";
import { Button } from "../components/ui/button";
import AdminMessageCallout from "../components/AdminMessageCallout";

const CATEGORIES = [
  { value: "merch", label: "Merch (apparel, accessories, etc.)" },
  { value: "apparel", label: "Apparel" },
  { value: "accessory", label: "Accessory" },
  { value: "digital", label: "Digital download" },
  { value: "physical", label: "Physical good" },
  { value: "collectible", label: "Collectible" },
];

const CorporateDashboard = () => {
  const navigate = useNavigate();
  const { token, user, logout } = useAuth();
  const [profile, setProfile] = useState(null);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [newProduct, setNewProduct] = useState({ name: "", description: "", price: "", category: "merch", stock: 0 });

  useEffect(() => {
    if (!token) { navigate("/auth"); return; }
    loadAll();
  }, [token]);

  const loadAll = async () => {
    try {
      const [meRes, productsRes] = await Promise.all([
        axios.get(`${API}/corporate/me`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/corporate/products/my`, { headers: { Authorization: `Bearer ${token}` } })
      ]);
      setProfile(meRes.data);
      setProducts(productsRes.data);
    } catch (e) {
      if (e.response?.status === 404) {
        toast.error("No corporate profile found. Sign in with a corporate account.");
        navigate("/home");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!newProduct.name.trim() || !newProduct.price) return;
    setCreating(true);
    try {
      const res = await axios.post(`${API}/corporate/products`, {
        name: newProduct.name.trim(),
        description: newProduct.description.trim(),
        price: parseFloat(newProduct.price),
        category: newProduct.category,
        stock: parseInt(newProduct.stock) || 0
      }, { headers: { Authorization: `Bearer ${token}` } });
      setProducts(prev => [res.data, ...prev]);
      setNewProduct({ name: "", description: "", price: "", category: "merch", stock: 0 });
      toast.success(`${res.data.name} added to your store`);
    } catch (e2) {
      toast.error(e2.response?.data?.detail || "Could not create product");
    } finally {
      setCreating(false);
    }
  };

  const handleDelete = async (productId) => {
    if (!window.confirm("Remove this product from your store?")) return;
    try {
      await axios.delete(`${API}/products/${productId}`, { headers: { Authorization: `Bearer ${token}` } });
      setProducts(prev => prev.filter(p => p.product_id !== productId));
      toast.success("Removed");
    } catch {
      toast.error("Failed to delete");
    }
  };

  if (loading) return <div className="min-h-screen bg-[#000000] flex items-center justify-center"><Loader2 className="w-8 h-8 animate-spin text-[#EC008C]" /></div>;
  if (!profile) return null;

  const isSuspended = !profile.store_active;

  return (
    <div className="min-h-screen bg-[#000000] workspace-surface" data-testid="corporate-dashboard">
      {/* Header */}
      <header className="glass-strong sticky top-0 z-30 border-b border-[#1A2030]">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/home" className="flex items-center gap-3 min-w-0 flex-1" data-testid="corporate-home-link">
            <img src="/logo.png" alt="" className="w-10 h-10 object-contain" style={{ mixBlendMode: "lighten" }} />
            <div className="min-w-0">
              <p className="font-['Unbounded'] text-lg font-bold text-white leading-none">Sponsors</p>
              <p className="text-xs text-[#8A92A0] truncate">{profile.company_name}</p>
            </div>
          </Link>
          <div className="flex items-center gap-3">
            <button onClick={() => { logout(); navigate("/"); }} className="text-sm text-[#8A92A0] hover:text-white flex items-center gap-1" data-testid="corporate-logout-btn">
              <LogOut className="w-4 h-4" /> Sign out
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-6xl mx-auto px-6 py-8 space-y-6">
        <AdminMessageCallout />
        <nav className="dashboard-section-navigation" aria-label="Sponsor workspace" data-testid="corporate-grouped-navigation">
          <section data-testid="corporate-navigation-business">
            <h2 data-testid="corporate-navigation-business-heading">Business profile</h2>
            <a href="#corporate-profile" className="dashboard-section-link" data-testid="corporate-profile-link"><Building2 className="w-4 h-4" />Company details</a>
          </section>
          <section data-testid="corporate-navigation-store">
            <h2 data-testid="corporate-navigation-store-heading">Manage your store</h2>
            <div className="flex flex-wrap gap-2">
              <a href="#corporate-products" className="dashboard-section-link" data-testid="corporate-products-link"><Package className="w-4 h-4" />Your products</a>
              {!isSuspended && <a href="#corporate-new-product" className="dashboard-section-link" data-testid="corporate-new-product-link"><Plus className="w-4 h-4" />Add a product</a>}
            </div>
          </section>
          <section data-testid="corporate-navigation-public">
            <h2 data-testid="corporate-navigation-public-heading">Public storefront</h2>
            <Link to={`/corporate/${profile.corporate_id}`} target="_blank" className="dashboard-section-link" data-testid="view-public-store">View store <ExternalLink className="w-3.5 h-3.5" /></Link>
          </section>
        </nav>
        {/* Suspended banner */}
        {isSuspended && (
          <div className="card p-5 border-[#D14A85]/40 bg-[#D14A85]/5" data-testid="suspended-banner">
            <div className="flex items-start gap-3">
              <AlertTriangle className="w-5 h-5 text-[#D14A85] flex-shrink-0 mt-0.5" />
              <div>
                <h3 className="font-semibold text-white mb-1">Your store is suspended pending review</h3>
                <p className="text-sm text-[#8A92A0]">{profile.ai_flag_reason || "Our team is reviewing your account. You'll be notified once it's approved."}</p>
              </div>
            </div>
          </div>
        )}

        {/* Profile summary */}
        <div id="corporate-profile" className="card p-6" data-testid="corporate-profile-card">
          <div className="flex items-start gap-4">
            <div className="w-16 h-16 rounded-2xl bg-[#EC008C]/15 border border-[#EC008C]/30 flex items-center justify-center flex-shrink-0">
              <Building2 className="w-8 h-8 text-[#EC008C]" />
            </div>
            <div className="flex-1">
              <h1 className="font-['Unbounded'] text-2xl font-bold text-white mb-1">{profile.company_name}</h1>
              <p className="text-sm text-[#8A92A0]">Contact: {profile.contact_name} · {user?.email}</p>
              {profile.business_website && (
                <a href={profile.business_website} target="_blank" rel="noopener noreferrer" className="text-sm text-[#EC008C] hover:text-[#FF3399] flex items-center gap-1 mt-1">
                  {profile.business_website.replace(/^https?:\/\//, "")} <ExternalLink className="w-3 h-3" />
                </a>
              )}
            </div>
            <div className="text-right">
              <span className={`px-3 py-1 rounded-full text-xs font-bold ${profile.ai_flag_status === "clean" ? "bg-[#8DB87A]/15 text-[#8DB87A]" : "bg-[#D14A85]/15 text-[#D14A85]"}`} data-testid="profile-status">
                {profile.ai_flag_status === "clean" ? "Verified" : profile.ai_flag_status}
              </span>
            </div>
          </div>
        </div>

        {/* Create new product */}
        {!isSuspended && (
          <div id="corporate-new-product" className="card p-6" data-testid="create-product-card">
            <h2 className="font-['Unbounded'] text-lg font-semibold text-white mb-4 flex items-center gap-2">
              <Plus className="w-5 h-5 text-[#EC008C]" /> Add a product to your store
            </h2>
            <form onSubmit={handleCreate} className="space-y-3">
              <input
                value={newProduct.name}
                onChange={e => setNewProduct({ ...newProduct, name: e.target.value })}
                placeholder="Product name"
                required
                className="w-full bg-[#0A0E18] border border-[#1A2030] rounded-lg px-3 py-2.5 text-sm text-white"
                data-testid="product-name"
              />
              <textarea
                value={newProduct.description}
                onChange={e => setNewProduct({ ...newProduct, description: e.target.value })}
                placeholder="Description"
                rows={2}
                className="w-full bg-[#0A0E18] border border-[#1A2030] rounded-lg px-3 py-2.5 text-sm text-white resize-none"
                data-testid="product-description"
              />
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <input
                  type="number"
                  step="0.01"
                  min="0.01"
                  value={newProduct.price}
                  onChange={e => setNewProduct({ ...newProduct, price: e.target.value })}
                  placeholder="Price (USD)"
                  required
                  className="bg-[#0A0E18] border border-[#1A2030] rounded-lg px-3 py-2.5 text-sm text-white"
                  data-testid="product-price"
                />
                <input
                  type="number"
                  min="0"
                  value={newProduct.stock}
                  onChange={e => setNewProduct({ ...newProduct, stock: e.target.value })}
                  placeholder="Stock"
                  className="bg-[#0A0E18] border border-[#1A2030] rounded-lg px-3 py-2.5 text-sm text-white"
                  data-testid="product-stock"
                />
                <select
                  value={newProduct.category}
                  onChange={e => setNewProduct({ ...newProduct, category: e.target.value })}
                  className="bg-[#0A0E18] border border-[#1A2030] rounded-lg px-3 py-2.5 text-sm text-white"
                  data-testid="product-category"
                >
                  {CATEGORIES.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
                </select>
              </div>
              <Button type="submit" disabled={creating} className="bg-[#EC008C] text-white hover:bg-[#FF3399]" data-testid="create-product-btn">
                {creating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
                {creating ? "Adding..." : "Add to store"}
              </Button>
            </form>
          </div>
        )}

        {/* Product list */}
        <div id="corporate-products" className="card p-6" data-testid="corporate-products-section">
          <h2 className="font-['Unbounded'] text-lg font-semibold text-white mb-4 flex items-center gap-2">
            <Package className="w-5 h-5 text-[#EC008C]" /> Your products
            <span className="text-xs text-[#8A92A0] font-normal ml-1">({products.length})</span>
          </h2>
          {products.length === 0 ? (
            <div className="text-center py-10 text-[#8A92A0]" data-testid="no-products-state">
              <ShoppingBag className="w-10 h-10 mx-auto mb-3 opacity-50" />
              No products yet. Add one above to start your store.
            </div>
          ) : (
            <div className="space-y-2">
              {products.map(p => (
                <div key={p.product_id} className="bg-[#0A0E18] rounded-lg p-4 border border-[#1A2030] flex items-center gap-4" data-testid={`product-row-${p.product_id}`}>
                  <div className="w-12 h-12 rounded bg-[#1A2030] flex items-center justify-center flex-shrink-0">
                    <Briefcase className="w-5 h-5 text-[#8A92A0]" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-white font-semibold truncate">{p.name}</p>
                    <p className="text-xs text-[#8A92A0]">{p.category} · stock {p.stock}</p>
                  </div>
                  <p className="text-[#EC008C] font-bold">${parseFloat(p.price).toFixed(2)}</p>
                  <button onClick={() => handleDelete(p.product_id)} className="text-[#8A92A0] hover:text-[#D14A85] p-1" data-testid={`delete-${p.product_id}`}>
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Fee callout */}
        <div className="card p-5 bg-[#EC008C]/5 border-[#EC008C]/30 text-center" data-testid="fee-callout">
          <p className="text-sm text-[#8A92A0]">You keep <span className="text-[#EC008C] font-bold">90%</span> of every sale. Platform fee is 10%. Payouts via your linked bank account.</p>
        </div>
      </div>
    </div>
  );
};

export default CorporateDashboard;
